import WithGuard from 'HOCs/withGuard'
import ButtonBase from 'components/common/ButtonBase'
import { useStores } from 'hooks/useStores'
import { inject, observer } from 'mobx-react'
import type _Account from 'mobx/Account'
import CouponError, { ErrorCode } from 'mobx/Coupons/errors'
import type _Infra from 'mobx/Infra'
import { Guard, SnackbarStatus } from 'mobx/Infra/Infra.type'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import type { Coupon, NonExternalCoupon } from 'shared-types/Coupon'
import { getTranslatedTextByKey } from 'utils/utils'
import { isExternal } from 'mobx/Coupons/utils'

interface ElementProps {
	disabled?: boolean
	onClick?: React.MouseEventHandler<HTMLButtonElement>
}

type ElementType = React.FC<React.PropsWithChildren<ElementProps>>

export enum ButtonType {
	UNAPPLY = 'unapply',
	APPLY = 'apply',
	VIEW = 'view',
	SAVE_AND_APPLY = 'saveAndApply',
	SIGN_IN_SAVE_AND_APPLY = 'saveSignInAndApply',
	SAVE_AND_CLOSE = 'saveAndClose',
	SIGN_IN_AND_APPLY = 'signInToApply',
	SIGN_IN_AND_VIEW = 'signInToView',
	ADD_TO_CART = 'addToCart',
	SAVE_AND_ADD_TO_CART = 'saveAndAddToCart',
	SIGN_IN_AND_ADD_TO_CART = 'signInAndAddToCart',
	SIGN_IN_SAVE_AND_ADD_TO_CART = 'saveSignInAndAddToCart',
	REMOVE_FROM_CART = 'removeFromCart',
	NONE = 'none',
}

export enum Origin {
	CARD,
	MODAL,
}

interface Stores {
	Infra: typeof _Infra
}

interface Props {
	coupon: Coupon
	disabled?: boolean
	Element?: ElementType
	onError: (error: CouponError, actions: Record<ButtonType, () => Promise<void>>, disableSnackbar?: boolean) => void
	onApply?: (coupon: NonExternalCoupon) => void
	variant: ButtonType
	origin: Origin
	textOverride?: ButtonType
}

interface ButtonDescriptor {
	text: string
	disableSnackbar?: boolean
	callback: (event: React.MouseEvent<HTMLButtonElement>) => Promise<unknown>
}

const Button = styled(ButtonBase)`
	display: flex;

	padding: 0.5rem 3rem;
`

const translationKey = 'eCommerce.coupons.modal'

const CouponButton: React.FC<Props & Stores> = ({
	coupon,
	onError,
	onApply,
	disabled = false,
	Element = Button,
	Infra,
	textOverride,
	variant,
	origin,
}) => {
	const walletEnabled = Infra.hasFeatureFlag('wallet')
	const { couponsStore, couponFlowStore } = useStores()

	const addToWallet = useCallback(
		async (showSnackbar = true): Promise<Coupon> => {
			if (!walletEnabled) {
				console.debug(`[${addToWallet.name}] Exiting early because feature flag is disabled`)
				return coupon
			}

			if (isExternal(coupon)) {
				return coupon
			}

			console.debug(`[${addToWallet.name}] Proceeding to add ${coupon.code} to wallet`)

			const couponToApply = await couponsStore.linkCoupon(coupon.code)

			if (showSnackbar) {
				Infra?.showSnackbar({
					status: SnackbarStatus.SUCCESS,
					message: getTranslatedTextByKey('eCommerce.coupons.snackbar.addedToWallet', 'The coupon was added to your wallet'),
					key: 'coupon-added-to-wallet',
					snackId: 'coupon-added-to-wallet',
					isAttachedToElement: false,
				})
			}

			console.debug(`[${addToWallet.name}] Successfully added ${coupon.code} to wallet`)

			return couponToApply
		},
		[Infra, coupon, couponsStore, walletEnabled]
	)

	const apply = useCallback(
		async (couponToApply: Coupon): Promise<boolean> => {
			if (isExternal(couponToApply)) {
				return false
			}

			onApply?.(couponToApply)

			return origin === Origin.CARD
				? couponFlowStore.start({
						code: couponToApply.code,
						openModal: false,
						orderTypeToForce: null,
					})
				: couponFlowStore.applyFromCouponModal(couponToApply)
		},
		[couponFlowStore, onApply, origin]
	)

	const unapply = useCallback(
		async (couponToUnapply: Coupon) => {
			await couponFlowStore.unapply(couponToUnapply)
		},
		[couponFlowStore]
	)

	const openModal = useCallback(() => Promise.resolve(couponsStore.setCouponModal(coupon)), [coupon, couponsStore])

	const buttonsMap: Record<ButtonType, ButtonDescriptor> = useMemo(
		() => ({
			[ButtonType.UNAPPLY]: {
				text: 'Unapply',
				callback: () => unapply(coupon),
			},
			[ButtonType.APPLY]: { text: 'Apply', callback: () => apply(coupon) },
			[ButtonType.VIEW]: { text: 'View', callback: openModal },
			[ButtonType.SAVE_AND_APPLY]: { text: 'Save & Apply', callback: () => addToWallet().then(apply) },
			[ButtonType.SAVE_AND_CLOSE]: {
				text: 'Save & Close',
				callback: async () => {
					couponFlowStore.clearCouponToApply()
					await addToWallet()
					return couponsStore.setCouponModal(null)
				},
			},
			[ButtonType.SIGN_IN_AND_VIEW]: {
				text: 'Sign In to View',
				callback: openModal,
			},
			[ButtonType.SIGN_IN_AND_APPLY]: { text: 'Sign In to Apply', callback: () => apply(coupon) },
			[ButtonType.SIGN_IN_SAVE_AND_APPLY]: {
				text: 'Sign In to Save & Apply',
				callback: () => addToWallet().then(apply),
			},
			[ButtonType.ADD_TO_CART]: {
				text: 'Add to Cart',
				callback: () => apply(coupon),
			},
			[ButtonType.REMOVE_FROM_CART]: {
				text: 'Remove from Cart',
				callback: () => unapply(coupon),
			},
			[ButtonType.SAVE_AND_ADD_TO_CART]: {
				text: 'Save & Add to Cart',
				callback: () => addToWallet(false).then(apply),
			},
			[ButtonType.SIGN_IN_AND_ADD_TO_CART]: {
				text: 'Sign In to Add to Cart',
				callback: () => addToWallet(false).then(apply),
			},
			[ButtonType.SIGN_IN_SAVE_AND_ADD_TO_CART]: {
				text: 'Sign In to Save & Add to Cart',
				callback: () => addToWallet(false).then(apply),
			},
			[ButtonType.NONE]: { text: '', callback: async () => {} },
		}),
		[addToWallet, apply, coupon, couponFlowStore, couponsStore, openModal, unapply]
	)

	const renderButton = (type: ButtonType) =>
		type === ButtonType.NONE ? null : (
			<Element
				disabled={disabled}
				onClick={(event) => {
					event.stopPropagation()
					const { callback } = buttonsMap[type]
					callback(event)
						.then((success) => {
							if (!success) {
								throw new CouponError('An error occured during apply', ErrorCode.DEFAULT)
							}
						})
						.catch((error) =>
							onError(
								error,
								// Actions to be executed from parent
								(Object.keys(buttonsMap) as ButtonType[]).reduce(
									(acc, curr) => ({ ...acc, [curr]: buttonsMap[curr].callback }),
									{} as Record<ButtonType, () => Promise<void>>
								)
							)
						)
				}}
				data-testid="coupon-apply-button"
			>
				{getTranslatedTextByKey(`${translationKey}.${textOverride ?? type}`, buttonsMap[textOverride ?? type].text)}
			</Element>
		)

	const signupTitle = getTranslatedTextByKey('eCommerce.signIn.couponWalletTitleText', 'Unlock Your Exclusive Coupons!')
	const signupSubTitle = getTranslatedTextByKey(
		'eCommerce.signIn.couponWalletSubTitleText',
		'Log in to access your exclusive coupons and enjoy great deals'
	)

	return coupon.flags.requireLogin?.value ? (
		<WithGuard
			context={Guard.Context.wallet}
			type={Guard.Type.event}
			name={Guard.Name.applyCoupon}
			overrideSignupParams={{ signupTitle, signupSubTitle, signupDisplaySkipSection: false }}
		>
			{renderButton(variant)}
		</WithGuard>
	) : (
		renderButton(variant)
	)
}

export default inject('Infra')(observer(CouponButton)) as React.FC<Props>
